/**
 * Finds the `{{name}}` markers a query takes its run values through.
 *
 * `{{name}}` was chosen because it cannot collide with anything Athena accepts: a brace is not a
 * token Trino allows where an expression belongs — `SELECT {{x}}` fails to parse with "mismatched
 * input '{'" — so an unrendered marker can never reach Athena and look like it worked.
 *
 * Nothing here knows which names are legal — that is the caller's question. This module only
 * finds markers and skips the places a `{{` is just text.
 */

export interface FoundMarker {
  name: string;
  start: number;
  /** Offset one past the closing brace. */
  end: number;
}

export interface QueryMarkerScan {
  /** Every well-formed marker, in the order it appears, including repeats. */
  markers: FoundMarker[];
  /**
   * A `{{` that does not open a valid marker. Reported rather than ignored: left alone it reaches
   * Athena as a brace and comes back as a parse error that says nothing about markers.
   */
  malformed: { start: number }[];
}

// Surrounding whitespace is accepted because `{{ report_from_date }}` is what people type, and
// rejecting it as a malformed marker would be baffling.
const ANCHORED_MARKER = /^\{\{\s*([A-Za-z_][A-Za-z0-9_]*)\s*\}\}/;

export const scanQueryMarkers = (sql: string): QueryMarkerScan => {
  const scan: QueryMarkerScan = { markers: [], malformed: [] };

  for (const { start: spanStart, end: spanEnd } of codeSpans(sql)) {
    let cursor = spanStart;
    for (let open = sql.indexOf('{{', cursor); open !== -1 && open < spanEnd; open = sql.indexOf('{{', cursor)) {
      const match = ANCHORED_MARKER.exec(sql.slice(open, spanEnd));
      if (!match) {
        scan.malformed.push({ start: open });
        cursor = open + 2;
        continue;
      }
      const [text, name] = match;
      scan.markers.push({ name, start: open, end: open + text.length });
      cursor = open + text.length;
    }
  }

  return scan;
};

/** Deduped marker names, in the order they first appear in the SQL. */
export const queryMarkerNamesUsed = (sql: string): string[] => [
  ...new Set(scanQueryMarkers(sql).markers.map((marker) => marker.name)),
];

/**
 * Finds reads of the query clock. What to make of them is the caller's policy; recognising them
 * lives here because it needs this module's notion of which text is code.
 *
 * Deliberately lexical, and only as good as that makes it: someone set on reaching the clock can
 * do so through something this does not recognise.
 */
const CLOCK_KEYWORD = /\b(current_timestamp|current_time|current_date|localtimestamp|localtime|now)\b/gi;

export interface FoundClockRead {
  /** Lower-cased function name, e.g. 'current_date'. */
  name: string;
  start: number;
  /** Offset one past the last character of the name. */
  end: number;
}

/** Every clock read in the SQL, in order, including repeats — offsets let a caller underline each. */
export const findClockFunctions = (sql: string): FoundClockRead[] => {
  const found: FoundClockRead[] = [];
  for (const { start, end } of codeSpans(sql)) {
    const code = sql.slice(start, end);
    for (const match of code.matchAll(CLOCK_KEYWORD)) {
      const name = match[1].toLowerCase();
      // The keywords read the clock bare, but `now` is only a clock read as a call — `now` alone
      // is a legal column name.
      if (name === 'now' && !/^\s*\(/.test(code.slice(match.index + match[1].length))) {
        continue;
      }
      found.push({ name, start: start + match.index, end: start + match.index + match[1].length });
    }
  }
  return found;
};

/** Deduped, lower-cased names of the clock functions the SQL reads. */
export const clockFunctionNamesUsed = (sql: string): string[] => [
  ...new Set(findClockFunctions(sql).map((read) => read.name)),
];

/**
 * An optional `[[ ... ]]` section
 *
 * A clause the author writes once and a run keeps only when the
 * parameters inside it are supplied.
 */
export interface FoundSection {
  /** Offset of the opening `[[`. */
  start: number;

  /** Offset one past the closing `]]`. */
  end: number;

  /** Offset of the first character after `[[`. */
  contentStart: number;

  /** Offset of the closing `]]` (one past the last content character). */
  contentEnd: number;

  /** The markers inside the section, in order, including repeats. */
  markers: FoundMarker[];

  /** Deduped names of the markers inside, in first-appearance order. */
  markerNames: string[];
}

export type SectionProblem =
  | { kind: 'nested'; start: number }
  | { kind: 'unclosed'; start: number }
  | { kind: 'unmatched-close'; start: number }
  | { kind: 'no-param'; start: number; end: number };

export interface OptionalSectionScan {
  sections: FoundSection[];
  problems: SectionProblem[];
}

type Token = { kind: 'open' | 'close'; pos: number };

function getSectionTokens(sql: string): Token[] {
  const spans = codeSpans(sql);

  const inCode = (pos: number): boolean => spans.some((span) => pos >= span.start && pos < span.end);
  const isWhitespace = (char: string | undefined): boolean => char !== undefined && /\s/.test(char);

  const tokens: { kind: 'open' | 'close'; pos: number }[] = [];

  for (let i = 0; i + 1 < sql.length; i += 1) {
    if (sql[i] === '[' && sql[i + 1] === '[' && inCode(i) && isWhitespace(sql[i + 2])) {
      tokens.push({ kind: 'open', pos: i });
      i += 1;
    } else if (sql[i] === ']' && sql[i + 1] === ']' && inCode(i) && isWhitespace(sql[i - 1])) {
      tokens.push({ kind: 'close', pos: i });
      i += 1;
    }
  }

  return tokens;
}

/**
 * Finds the optional `[[ ... ]]` sections a query declares.
 *
 * The delimiters are the doubled brackets `[[` and `]]`, but only when whitespace exists after the open
 * and before the close, to avoid collisions with existing bracket syntax (ex: `ARRAY[[1,2]]`, `m['k']`
 */
export const scanOptionalSections = (sql: string): OptionalSectionScan => {
  const tokens = getSectionTokens(sql);
  const { markers } = scanQueryMarkers(sql);

  const sections: FoundSection[] = [];
  const problems: SectionProblem[] = [];
  const openStack: number[] = [];

  for (const token of tokens) {
    if (token.kind === 'open') {
      if (openStack.length > 0) {
        problems.push({ kind: 'nested', start: token.pos });
      }
      openStack.push(token.pos);
      continue;
    }

    const open = openStack.pop();
    if (open === undefined) {
      problems.push({ kind: 'unmatched-close', start: token.pos });
      continue;
    }

    // Inner close of a nested pair — the nested problem already reported it; do not form a section.
    if (openStack.length > 0) {
      continue;
    }

    const start = open;
    const end = token.pos + 2;
    const contentStart = start + 2;
    const contentEnd = token.pos;

    const inside = markers.filter((marker) => marker.start >= contentStart && marker.end <= contentEnd);
    if (inside.length === 0) {
      problems.push({ kind: 'no-param', start, end });
      continue;
    }

    sections.push({
      start,
      end,
      contentStart,
      contentEnd,
      markers: inside,
      markerNames: [...new Set(inside.map((marker) => marker.name))],
    });
  }

  for (const open of openStack) {
    problems.push({ kind: 'unclosed', start: open });
  }

  return { sections, problems };
};

/**
 * Checks for correctly opened and closed parenthesis in a string
 */
export const hasSelfContainedParens = (text: string): boolean => {
  let depth = 0;

  for (const { start, end } of codeSpans(text)) {
    for (let i = start; i < end; i += 1) {
      if (text[i] === '(') {
        depth += 1;
      } else if (text[i] === ')') {
        depth -= 1;
        if (depth < 0) {
          // Closes a paren opened before the text — it borrows structure from its surroundings.
          return false;
        }
      }
    }
  }

  return depth === 0;
};

/** Whether `text`, from its first non-whitespace code character, matches `pattern`. */
export const firstCodeContentMatches = (text: string, pattern: RegExp): boolean => {
  for (const { start, end } of codeSpans(text)) {
    const slice = text.slice(start, end);
    const leadingWhitespace = slice.length - slice.replace(/^\s+/, '').length;
    if (leadingWhitespace === slice.length) {
      continue;
    }
    return pattern.test(text.slice(start + leadingWhitespace, end));
  }
  return false;
};

interface CodeSpan {
  start: number;
  end: number;
}

/**
 * The regions of the SQL that are code, leaving out single-quoted strings, double-quoted
 * identifiers, `--` line comments and `/* *\/` blocks. A marker or a clock function inside any of
 * those is text the author wrote on purpose — and a quoted identifier is also the escape hatch for
 * a column genuinely named `current_date`.
 */
const codeSpans = (sql: string): CodeSpan[] => {
  const spans: CodeSpan[] = [];
  let spanStart = 0;
  let cursor = 0;

  while (cursor < sql.length) {
    if (startsNonCode(sql, cursor)) {
      spans.push({ start: spanStart, end: cursor });
      cursor = endOfNonCode(sql, cursor);
      spanStart = cursor;
    } else {
      cursor += 1;
    }
  }

  spans.push({ start: spanStart, end: sql.length });
  return spans;
};

const startsNonCode = (sql: string, at: number): boolean =>
  sql.startsWith('--', at) || sql.startsWith('/*', at) || sql[at] === "'" || sql[at] === '"';

const endOfNonCode = (sql: string, start: number): number => {
  if (sql.startsWith('--', start)) {
    const newline = sql.indexOf('\n', start);
    return newline === -1 ? sql.length : newline + 1;
  }
  if (sql.startsWith('/*', start)) {
    const close = sql.indexOf('*/', start + 2);
    return close === -1 ? sql.length : close + 2;
  }

  const quote = sql[start];
  let cursor = start + 1;
  while (cursor < sql.length) {
    if (sql[cursor] === quote) {
      // Doubling is the only escape in both grammars, so a pair continues the literal.
      if (sql[cursor + 1] === quote) {
        cursor += 2;
        continue;
      }
      return cursor + 1;
    }
    cursor += 1;
  }
  // Unterminated: treat the rest as non-code. Athena will reject the statement anyway, and
  // scanning on would find "markers" inside what the author meant as a string.
  return sql.length;
};
